import React, {createContext, useContext, useEffect, useState} from 'react';
import {onAuthStateChanged} from 'firebase/auth';
import {auth} from '../../firebase';
import {apiFetch} from '../../api/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const saved = localStorage.getItem("quickserve_user");
        return saved ? JSON.parse(saved) : null;
    });
    const [isLoading, setIsLoading] = useState(true);
    const [emailVerified, setEmailVerified] = useState(false);

    const normalizeUser = (u) => {
        const fullName =
            u?.name ??
            `${u?.firstName || ""} ${u?.lastName || ""}`.trim();

        const skills =
            Array.isArray(u?.skills) ? u.skills :
                (u?.position ? [u.position] : []);

        return {
            ...u,
            name: fullName,
            skills,
            avatar: u?.avatar || u?.profilePicUrl || "",
        };
    };

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    // Fetch user data from backend using Firebase UID
    const fetchUserData = async () => {
        const maxAttempts = 6;
        const baseDelayMS = 250;

        for (let attempt = 1; attempt <= maxAttempts; attempt++){
            try {
                const res = await apiFetch("/api/users/me", { method: "GET",});
                return await res.json();
            } catch (error) {
                if (error?.status === 404 && attempt < maxAttempts) {
                    await sleep(baseDelayMS * attempt);
                    continue;
                }
                console.error("Failed to fetch user data:", error);
                return null;
            }
        }
        return null;
    };

    useEffect(() => {
        // Set up Firebase auth state listener
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setIsLoading(true);

            if (firebaseUser) {
                // User is signed in with Firebase
                try {
                    // Try to fetch user data from backend
                    await firebaseUser.reload();
                    setEmailVerified(!!firebaseUser.emailVerified);
                    const backendUser = await fetchUserData();

                    if (backendUser) {
                        const normalized = normalizeUser(backendUser);
                        setUser(normalized);
                        localStorage.setItem('quickserve_user', JSON.stringify(normalized));
                    } else {
                        setUser(null);
                        localStorage.removeItem('quickserve_user');
                    }
                } catch (error) {
                    console.error("Error in auth state change:", error);
                    setEmailVerified(!!firebaseUser?.emailVerified);
                    // Fallback to localStorage if available
                    const savedUser = localStorage.getItem('quickserve_user');
                    if (savedUser) {
                        setUser(JSON.parse(savedUser));
                    } else {
                        setUser(null);
                    }
                }
            } else {
                // User is signed out
                setUser(null);
                setEmailVerified(false);
                localStorage.removeItem('quickserve_user');
            }

            setIsLoading(false);
        });

        // Cleanup subscription
        return () => unsubscribe();
    }, []);

    const login = (userData) => {
        const normalized = normalizeUser(userData);
        setUser(normalized);
        localStorage.setItem('quickserve_user', JSON.stringify(normalized));
    };

    const logout = async () => {
        try {
            await auth.signOut();
        } catch (error) {
            console.error("Error signing out:", error);
        }
        setUser(null);
        localStorage.removeItem('quickserve_user');
    };

    const updateUser = async (updatedData) => {
        const optimistic = {...user, ...updatedData };
        setUser(optimistic);
        localStorage.setItem('quickserve_user', JSON.stringify(optimistic));

        //Ako firebase nije ucitan, zaustavi se ovde
        const firebaseUser = auth.currentUser;
        if(!firebaseUser) return optimistic;

        try {
            const token = await firebaseUser.getIdToken();
            const res = await apiFetch(`/api/users/me`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(updatedData),
            });

            if(!res.ok) {
                setUser(user);
                localStorage.setItem('quickserve_user', JSON.stringify(user));
                throw new Error(await res.text());
            }

            const saved = await res.json();
            const normalized = normalizeUser(saved);

            setUser(normalized);
            localStorage.setItem('quickserve_user', JSON.stringify(normalized));
            return normalized;
        } catch (error) {
            console.error("Error in auth state change:", error);
            throw error;
        }
    };

    const authReady = !isLoading;

    const getToken = async () => {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) return null;
        return firebaseUser.getIdToken();
    }

    const value = {
        user,
        authReady,
        isLoggedIn: authReady && !!user,
        isEmployer: user?.role === 'employer',
        isWorker: user?.role === 'worker',
        isLoading,
        emailVerified,
        login,
        logout,
        updateUser,
        getToken,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;